import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';

// Components
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import KOGCardStatus from '@/main/components/shared/cardStatus/cardStatus';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import CardGridOrder from './components/cardGrid/cardGrid';
import VehicleAutoCompleteComponent from '@/main/components/shared/vehicle/VehicleAutoCompleteComponent';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import KogKeyboardDatePickerContainer from '@/main/components/shared/datepicker/keyboardDatePickerContainer';

// Api's
import { packingTrackingApi } from '@/main/api';

// Models
import { KeyValue } from '@/main/models/common';
import { BodyShopFilterRequest } from '@/main/models/request/bodyshopRequest';
import {
  InsurerFilterRequest,
  ReasonFilterRequest,
  SupplierFilterRequest
} from '@/main/models/request';
import {
  GetItemsBySupplierResponse,
  GetPackageManagementResponse,
  VehicleAutoCompleteResponse
} from '@/main/models/response';
import { getManagePackagesStatus } from '@/main/services/data/data';
import { GetPackageManagementFilterRequest } from '@/main/models/request/packingTrackingRequest';

// Utils & Enums
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { VehicleAutoCompleteTypeEnum } from '@/main/services/enum/VehicleAutoCompleteTypeEnum';
import {
  getDefaultCountryOptionDrop,
  getDefaultMessageResultsFound,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  mountReducerWithData,
  removeEmptyValues
} from '@/main/services/utils';
import {
  finalDateForm,
  getRangeInitialFinalDate,
  initalDateForm,
  onLoadDateRangeBetweenPicker,
  TypeKeysValidationsDate,
  validateFormDate
} from '@/main/services/utilsDate';
import {
  userIsBodyshop,
  userIsInsurer,
  userIsSupplier
} from '@/main/services/utilsUser';
import {
  formatDateToISOString
} from '@/main/services/utilsDate';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';

// Redux & Hooks
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getSuppliers,
  resetReducerSuppliers,
  useSupplierReducer
} from '@/main/store/supplier';
import { getReason, useReasonReducer } from '@/main/store/reason';
import {
  useManagePackagesCountStatusReducer,
  getManagePackagesCountStatus
} from '@/main/store/managePackagesCountStatus';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';

// Styles
import * as S from './styles';
import { KOGAreaButtons } from '@/main/components/shared/styles';

export interface IGridData {
  data: GetPackageManagementResponse[];
  totalItems: number;
  isLoading: boolean;
}

export interface IGridDataTracking {
  data: GetItemsBySupplierResponse[];
  totalItems: number;
  isLoading: boolean;
}


function ManagePackagesScreen() {
  // Reducers
  const dispatch = useDispatch();
  const { insurers: insurerReducer } = useInsurerReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { bodyshops: bodyshopReducer } = useBodyshopReducer();
  const { suppliers: supplierReducer } = useSupplierReducer();
  const { reason: reasonReducer } = useReasonReducer();
  const {
    managePackagesCountStatus: managePackagesCountStatusReducer
  } = useManagePackagesCountStatusReducer();
  const statusManagePackages = getManagePackagesStatus();
  const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchPackageManageDaysInterval);
  const rangeBetween = getRangeInitialFinalDate(parameterRangeDays)

  const INITIAL_FORM_STATE = {
    quoteFilterVehicle: '',
    startDate: initalDateForm(rangeBetween.initialRange),
    finalDate: finalDateForm(rangeBetween.finalRange),
    status: null as KeyValue | null,
    reason: null as KeyValue | null,
    insurer: null as KeyValue | null,
    supplier: null as KeyValue | null,
    bodyshopBranch: null as KeyValue | null,
    trackingNumber: '' as string,
    subModel: '' as string,
    vehicleManufacturer: '' as string,
    vehicleModel: '' as string,
    vehicle: '' as string
  };
  type ISearchPackageManagementForm = typeof INITIAL_FORM_STATE;
  const {
    control,
    queryParamsValues,
    customHandleSubmit,
    setValue,
    reset,
    clearErrors,
    getValues
  } = useFormQueryParams<ISearchPackageManagementForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      bodyshopBranch: bodyshopReducer,
      insurer: insurerReducer,
      supplier: supplierReducer,
      status: mountReducerWithData(statusManagePackages),
      reason: reasonReducer
    },
    onSubmit: onSubmit
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const cardStatusQueryParams = searchParams.get('idCardStatus');
  const ignoreKeyValidations: TypeKeysValidationsDate[] = ['finalDateGreaterThanCurrentDate'];

  // States
  const [isInitialLoadScreen, setIsInitialLoadScreen] = useState<boolean>(
    false
  );
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const INITIAL_GRID_STATE: IGridData = {
    totalItems: 0,
    isLoading: false,
    data: []
  };
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [
    formFilters,
    setFormFilters
  ] = useState<GetPackageManagementFilterRequest | null>(null);
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const [hasClear, setHasClear] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<
    boolean
  >(false);

  // Methods/Validatoin/Variables for Form
  const hasBodyshopDefault =
    userIsBodyshop() && bodyshopReducer?.data?.length === 1;
  const isUserSupplierLogged = userIsSupplier();
  const isUserInsurerLogged = userIsInsurer();
  const isUserBodyShopLogged = userIsBodyshop();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );
  const optionDefaultBodyshopDrop = getDefaultOptionDrop(bodyshopReducer.data);
  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierReducer.data);

  const isDisabledInsurersDrop = () => userIsInsurer();
  const isDisabledBodyshopDrop = () =>
    bodyshopReducer.data === null || hasBodyshopDefault;
  const isDisabledSupplierDrop = () =>
    supplierReducer.data === null || userIsSupplier();

  const titleGrid = getDefaultMessageResultsFound(
    gridData.data ? gridData.data.length : 0,
    gridData.totalItems ? gridData.totalItems : 0
  );
  const labelQuoteFilterVehicle = '{0} / {1} / {2}'
    .replace('{0}', getTranslateByKey('information.claimNr'))
    .replace('{1}', getTranslateByKey('information.orderNr'))
    .replace('{2}', getTranslateByKey('information.licensePlate'));

  function callInsurerApiDomain(companySelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerInsurers());

    if (optionDefaultCountryDrop !== null) {
      const request: InsurerFilterRequest = {
        ...(companySelected && { guidBodyShop: String(companySelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));
    }
  }

  function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerBodyshops());

    if (optionDefaultCountryDrop !== null) {
      const request: BodyShopFilterRequest = {
        ...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getBodyshops(request));
    }
  }

  function callSupplierApiDomain() {
    // Clean fields & reducers
    dispatch(resetReducerSuppliers());

    if (optionDefaultCountryDrop !== null) {
      const request: SupplierFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getSuppliers(request));
    }
  }

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function onSubmit(formInputs: ISearchPackageManagementForm) {
    validateFormDate(
      formInputs.startDate,
      formInputs.finalDate,
      parameterRangeDays,
      ignoreKeyValidations
    );

    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: GetPackageManagementFilterRequest = {
      startDate: formatDateToISOString(formInputs.startDate),
      finalDate: formatDateToISOString(formInputs.finalDate),
      trackingCode: formInputs.trackingNumber,
      quoteFilterVehicle: formInputs?.quoteFilterVehicle
        ? String(formInputs.quoteFilterVehicle)
        : null,
      guidBodyshopBranch: formInputs.bodyshopBranch?.key
        ? String(formInputs.bodyshopBranch.key)
        : null,
      guidInsurer: formInputs.insurer?.key
        ? String(formInputs.insurer.key)
        : null,
      guidVehicleManufacturer: formInputs.vehicleManufacturer
        ? String(formInputs.vehicleManufacturer)
        : null,
      guidVehicleModel: formInputs.vehicleModel
        ? String(formInputs.vehicleModel)
        : null,
      guidVehicleSubModel: formInputs.subModel
        ? String(formInputs.subModel)
        : null,
      packageStatus: formInputs.status?.key
        ? Number(formInputs.status.key)
        : null,
      guidReason: formInputs.reason?.key ? String(formInputs.reason.key) : null,
      guidSupplier: formInputs.supplier?.key
        ? String(formInputs.supplier.key)
        : null,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);

    packingTrackingApi.getPackageManagement(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results
      });
      setHasShowMore(
        response.data.results && response.data.totalNumberOfRecords
          ? response.data.results.length < response.data.totalNumberOfRecords
          : false
      );
    });
    setShowFilters(false);
  }

  function onLoadInfiniteScroll(request: GetPackageManagementFilterRequest) {
    setIsLoadingInfiniteScroll(true);
    packingTrackingApi
      .getPackageManagement(request)
      .then(response => {
        let resultData: GetPackageManagementResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize30 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: index
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    const hasSupplierDefault = userIsSupplier();
    if (hasSupplierDefault) setValue('supplier', optionDefaultSupplierDrop);
    const { firstDateResult } = onLoadDateRangeBetweenPicker(rangeBetween);
    const secondDateResult = finalDateForm(rangeBetween.finalRange);
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    clearErrors();
    setHasClear(true);

    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);

    if (optionDefaultCountryDrop !== null) {
      callBodyshopApiDomain();
      callInsurerApiDomain();
    } else {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
    }
  }

  function existsInDrop(data: KeyValue[], nameField: any) {
    const fieldKeySelected = getValues(nameField)?.key;
    const existsInData = data.find(item => item.key === fieldKeySelected);

    if (!existsInData) {
      setValue(nameField, null);
    }
  }

  // This function is used to create initial request with default values
  function constructInitialRequest(): ISearchPackageManagementForm {
    const request: ISearchPackageManagementForm = cloneDeep(INITIAL_FORM_STATE);
    const { firstDateResult } = onLoadDateRangeBetweenPicker(rangeBetween);
    const secondDateResult = finalDateForm(rangeBetween.finalRange);
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    request.startDate = firstDateResult;
    request.finalDate = secondDateResult;
    return cloneDeep(request);
  }

  const handleClickCard = (data: KeyValue) => {
    setValue('status', data);
    handleClear();
    let request: ISearchPackageManagementForm = getValues();
    request.status = data;
    setSearchParams(
      {
        idCardStatus: String(data.key)
      },
      { replace: true }
    );
    if (optionDefaultCountryDrop?.key) {
      onSubmit(request);
    }
  };

  function loadDataWithCardStatusParams() {
    const request = constructInitialRequest();
    const statusFilter: KeyValue | null = {
      key: String(cardStatusQueryParams),
      value: ''
    };
    request.status = statusFilter;
    dispatch(getManagePackagesCountStatus(request));
    onSubmit(request);
  }

  // This function is used to load the grid data when first loading the screen
  function loadInitialData(ignoreInitialLoadScreen: boolean = false) {
    if (
      (!isInitialLoadScreen || ignoreInitialLoadScreen) &&
      !isNullUndefined(parameterRangeDays)
    ) {
      const haveQueryParams = searchParams.toString() !== '';

      if (cardStatusQueryParams) {
        loadDataWithCardStatusParams();
      } // Load data with statusQueryParams
      else if (haveQueryParams) {
        handleUpdateQuoteCountStatus(
          queryParamsValues.startDate,
          queryParamsValues.finalDate
        );
      } // Load data with query params
      else {
        // Load data default
        const request = constructInitialRequest();
        // Call grid by User logged
        if (
          isUserSupplierLogged &&
          supplierReducer.data &&
          !supplierReducer.isLoading &&
          supplierReducer.data.length === 1
        ) {
          request.supplier = supplierReducer.data[0];
          dispatch(getManagePackagesCountStatus(request));
          if (optionDefaultCountryDrop?.key) {
            onSubmit(request);
          }
        } else if (
          isUserInsurerLogged &&
          insurerReducer.data &&
          !insurerReducer.isLoading &&
          insurerReducer.data.length === 1
        ) {
          request.insurer = insurerReducer.data[0];
          dispatch(getManagePackagesCountStatus(request));
          if (optionDefaultCountryDrop?.key) {
            onSubmit(request);
          }
        } else if (
          isUserBodyShopLogged &&
          bodyshopReducer.data &&
          !bodyshopReducer.isLoading &&
          bodyshopReducer.data.length === 1
        ) {
          request.bodyshopBranch = bodyshopReducer.data[0];
          dispatch(getManagePackagesCountStatus(request));
          if (optionDefaultCountryDrop?.key) {
            onSubmit(request);
          }
        }
        dispatch(getManagePackagesCountStatus(request));
        if (optionDefaultCountryDrop?.key) {
          onSubmit(request);
        }
      }

      setIsInitialLoadScreen(true);
    }
  }

  // Initial load default domains
  useEffect(() => {
    dispatch(getCountriesUser());
    return () => {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
      dispatch(resetReducerSuppliers());
    };
  }, []);

  // Load Domains by Country
  useEffect(() => {
    callSupplierApiDomain();
    callInsurerApiDomain();
    callBodyshopApiDomain();
    if (optionDefaultCountryDrop !== null) {
      const requestReason: ReasonFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        idReasonType: ReasonTypeEnum.QuoteChangeStatus
      };
      dispatch(getReason(requestReason));
    }
  }, [optionDefaultCountryDrop?.key]);

  useEffect(() => {
    loadInitialData();
  }, [
    insurerReducer.isLoading,
    bodyshopReducer.isLoading,
    supplierReducer.isLoading
  ]);

  useEffect(() => {
    if (insurerReducer.data && !insurerReducer.isLoading) {
      const isTypeCompanyUserInsurer = userIsInsurer();
      existsInDrop(insurerReducer.data, 'insurer');
      if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop)
        callBodyshopApiDomain(optionDefaultInsurerDrop);
    }
  }, [insurerReducer.data, insurerReducer.isLoading]);

  useEffect(() => {
    if (bodyshopReducer.data && !bodyshopReducer.isLoading) {
      const isTypeCompanyUserBodyshop = userIsBodyshop();
      existsInDrop(bodyshopReducer.data, 'bodyshopBranch');
      if (isTypeCompanyUserBodyshop && optionDefaultBodyshopDrop)
        callInsurerApiDomain(optionDefaultBodyshopDrop);
    }
  }, [bodyshopReducer.data, bodyshopReducer.isLoading]);

  function handleUpdateQuoteCountStatus(startDate?: Date, finalDate?: Date) {
    if (!isNullUndefined(parameterRangeDays)) {
      //#region Set Values in form
      const { firstDateResult } = onLoadDateRangeBetweenPicker(
        rangeBetween,
        startDate,
        finalDate
      );
      const secondDateResult = finalDateForm(rangeBetween.finalRange);

      setValue('startDate', firstDateResult);
      setValue('finalDate', secondDateResult);
      //#endregion

      const request: ISearchPackageManagementForm = {
        ...INITIAL_FORM_STATE,
        startDate: initalDateForm(parameterRangeDays),
        finalDate: finalDateForm()
      };

      if (
        isUserSupplierLogged &&
        supplierReducer.data &&
        !supplierReducer.isLoading &&
        supplierReducer.data.length === 1
      ) {
        request.supplier = supplierReducer.data[0];
        dispatch(getManagePackagesCountStatus(request));
      } else if (
        isUserInsurerLogged &&
        insurerReducer.data &&
        !insurerReducer.isLoading &&
        insurerReducer.data.length === 1
      ) {
        request.insurer = insurerReducer.data[0];
        dispatch(getManagePackagesCountStatus(request));
      } else if (
        isUserBodyShopLogged &&
        bodyshopReducer.data &&
        !bodyshopReducer.isLoading &&
        bodyshopReducer.data.length === 1
      ) {
        request.bodyshopBranch = bodyshopReducer.data[0];
        dispatch(getManagePackagesCountStatus(request));
      }
      dispatch(getManagePackagesCountStatus(request));
    }
  }

  function getStatusFiltered() {
    const managePackagesStatus = getManagePackagesStatus();
    if (managePackagesStatus !== null && managePackagesStatus.length > 0) {
      let data: KeyValue[] = [];
      if (isUserSupplierLogged) {
        data = getStatusFilteredBySupplierUser(managePackagesStatus);
      } else {
        data = managePackagesStatus;
      }
      return data.map(item => {
        const count =
          managePackagesCountStatusReducer.data &&
          managePackagesCountStatusReducer?.data.find(
            countItem => Number(countItem.keyStatus) === Number(item.key)
          )?.value;
        return {
          ...item,
          count: Number(count || 0)
        };
      });
    }

    return [];
  }

  function getStatusFilteredBySupplierUser(data: KeyValue[]) {
    return data.filter(
      (x: KeyValue) =>
        Number(x.key) !== ProcurementStatusEnum.Open &&
        Number(x.key) !== ProcurementStatusEnum.Manual
    );
  }

  return (
    <ContainerScreen
      title={`
      ${getTranslateByKey('information.home')}
      ${' > '}
      ${getTranslateByKey('information.managePackages')} `}>
      <S.Container>
        <S.CustomAreaButton>
          <Button
            onClick={handleFilters}
            id="id-searchManagePackages-btn-filters">
            {getTranslateByKey('information.filters')}
          </Button>
        </S.CustomAreaButton>
        <S.CardArea>
          {getStatusFiltered().map(headerStatus => {
            return (
              <KOGCardStatus
                key={headerStatus.key}
                value={headerStatus.value}
                idStatus={Number(headerStatus.key)}
                numberOfValues={headerStatus.count}
                onClick={() => handleClickCard(headerStatus)}
                isManagePackages
              />
            );
          })}
        </S.CardArea>

        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.Row>
              <KogInputTextControlledNew
                name="quoteFilterVehicle"
                label={labelQuoteFilterVehicle}
                control={control}
                id="id-searchManagePackages-txt-quoteFilterVehicle"
                autoComplete="off"
              />
              <KogKeyboardDatePickerContainer
                idScreen="id-searchManagePackages-date-searchOrder"
                nameStartDate="startDate"
                nameFinalDate="finalDate"
                control={control}
                ignoreKeyValidations={ignoreKeyValidations}
                parameterRangeDays={parameterRangeDays}
              />
              <VehicleAutoCompleteComponent
                hasClearValue={hasClear}
                setHasClearValue={setHasClear}
                handleChangeVehicle={(vehicle: VehicleAutoCompleteResponse) => {
                  setValue(
                    'vehicleManufacturer',
                    vehicle?.guidVehicleManufacturer
                  );
                  setValue('vehicleModel', vehicle?.guidVehicleModel);
                  setValue('subModel', vehicle?.guidSubModel);
                  setValue('vehicle', vehicle?.value);
                }}
                vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
              />
            </S.Row>
            <S.Row>
              <KogAutoCompleteControlled
                nameCustom="bodyshopBranch"
                id="id-searchManagePackages-select-bodyshopBranch"
                isDefaultValueUnique
                setValueCustom={setValue}
                label={getTranslateByKey('information.bodyshopBranch')}
                control={control}
                rulesCustom={{ required: false }}
                options={bodyshopReducer.data}
                isLoading={bodyshopReducer.isLoading}
                disabled={isDisabledBodyshopDrop()}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  const isTypeCompanyUserInsurer = userIsInsurer();
                  if (optionSelected === null) {
                    optionDefaultInsurerDrop != null
                      ? callBodyshopApiDomain(optionDefaultInsurerDrop)
                      : callBodyshopApiDomain();
                  }
                  if (!isTypeCompanyUserInsurer)
                    callInsurerApiDomain(optionSelected);
                }}
              />
              <KogAutoCompleteControlled
                nameCustom="insurer"
                id="id-searchManagePackages-select-insurer"
                isDefaultValueUnique={optionDefaultInsurerDrop !== null}
                setValueCustom={setValue}
                label={getTranslateByKey('information.insurer')}
                control={control}
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={insurerReducer.data}
                isLoading={insurerReducer.isLoading}
                disabled={isDisabledInsurersDrop()}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  if (optionSelected === null) {
                    optionDefaultBodyshopDrop != null
                      ? callInsurerApiDomain(optionDefaultBodyshopDrop)
                      : callInsurerApiDomain();
                  }
                  callBodyshopApiDomain(optionSelected);
                }}
              />
              <KogAutoCompleteControlled
                nameCustom="supplier"
                control={control}
                isDefaultValueUnique
                setValueCustom={setValue}
                id="id-searchManagePackages-select-supplier"
                label={getTranslateByKey('information.supplier')}
                rulesCustom={{ required: false }}
                options={supplierReducer.data}
                isLoading={supplierReducer.isLoading}
                disabled={isDisabledSupplierDrop()}
                onChangeCustom={() => { }}
              />
              <KogInputTextControlledNew
                label={getTranslateByKey(
                  'packingTrackingHistory.trackingNumber'
                )}
                name="trackingNumber"
                control={control}
                id="id-managePackages-txt-trackingNumber"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                control={control}
                nameCustom="status"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={statusManagePackages}
                onChangeCustom={() => { }}
                id="id-searchManagePackages-select-status"
              />
            </S.Row>
            <KOGAreaButtons>
              <SecondaryButton
                type="submit"
                id="id-searchManagePackages-btn-submit">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchManagePackages-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>
        <CardGridOrder
          title={titleGrid}
          formFilters={formFilters}
          gridData={gridData}
          infiniteScrollConfig={{
            dataLength: gridData.data ? gridData.data.length : 0,
            fetchData: () => onLoadSearchGrid(),
            hasMore: hasShowMore,
            isLoading: isLoadingInfiniteScroll
          }}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(ManagePackagesScreen);
